import { useRef } from 'react'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'

/**
 * HeroIntro - GSAP entrance animation for the HOME hero.
 *
 * Sequence (each element from 20px below + opacity 0, 0.7s, power2.out,
 * 0.15s between steps):
 *   eyebrow -> headline -> lead -> the two CTAs (staggered from each other)
 *   -> the AVA / Zentra / MyDart product rail.
 *
 * ── Why this is a DOM-querying component and not a scoped useGSAP ──────────
 * The hero is not React markup. It lives in Pentabay.html, which HtmlPage.jsx
 * injects with dangerouslySetInnerHTML, so there is no JSX to hang a scope ref
 * on. Every component in src/ works this way: React supplies behaviour, the
 * markup comes from the HTML layer. useGSAP is still doing real work here -
 * its context owns every tween created inside the callback and reverts them on
 * cleanup, which is what makes the "runs once" guarantee safe.
 *
 * ── Why it waits for the element ──────────────────────────────────────────
 * useGSAP runs in a LAYOUT effect; HtmlPage injects the body in a regular
 * effect. Layout effects fire first, so on the initial mount the hero does not
 * exist yet and a plain querySelector returns null. Hence the MutationObserver.
 * It is not a polling hack: observer callbacks are microtasks, delivered at the
 * end of the task that mutated the DOM and BEFORE the browser paints, so the
 * opening gsap.set() lands on the same frame the markup does. That is what
 * keeps the hero from flashing in at full opacity and then snapping to hidden.
 *
 * ── The globe highlight ───────────────────────────────────────────────────
 * The hero's only imagery is .hero-video (hero-loop.mp4) - a rotating Earth with
 * city lights and network arcs - shipped at opacity 0.25, which is faint enough
 * that it reads as stray arcs rather than a globe.
 *
 * It CANNOT simply be turned up. The hero copy is centred directly over the
 * brightest part of the frame, and measured worst-case contrast of white copy
 * against that field is already 4.8:1 for .t-lead at 0.25 - barely over the
 * 4.5:1 AA floor. At 0.35 the lead falls to 3.8:1 and the product tagline to
 * 4.5:1. The warning in StyleOverrides ("do not raise past 0.3") is correct.
 *
 * So the globe is highlighted where the copy ISN'T. HERO_INTRO_CSS raises the
 * plate to GLOBE_PEAK but masks it with a radial that holds the centre at ~30%,
 * so the copy bed lands DIMMER than it ships today while the limb and the arcs
 * roughly double. GSAP then reveals it (scale + fade, so it arrives rather than
 * being wallpaper) and breathes it very slowly.
 *
 * ── Why the word-split animation is switched off ──────────────────────────
 * runHeroChars() in Pentabay.html already animates this same <h1>: it splits
 * it into <span class="word"> elements (CSS: opacity 0, translateY(0.45em))
 * and staggers an .in class onto them. Animating the h1 as a single element,
 * as specified, would either double up with that or - if runHeroChars had not
 * run yet - fade in an h1 whose words are all still opacity 0, i.e. an empty
 * headline. Rather than race it, HERO_INTRO_CSS below neutralises .word inside
 * the home hero so the headline is one opaque block for GSAP to move. This
 * intentionally REPLACES the word-by-word reveal on the home hero; remove that
 * rule and drop the h1 from the timeline to get it back.
 */

// Targets, in sequence order. Resolved against the home hero only.
const STEPS = [
  { key: 'eyebrow',  sel: '.t-eyebrow' },
  { key: 'headline', sel: '[data-hero-h1]' },
  { key: 'lead',     sel: '.t-lead' },
  { key: 'ctas',     sel: '.btn', all: true },   // the two buttons, staggered
  { key: 'rail',     sel: '.hero-prods' },       // AVA, Zentra, MyDart, …
]

/* 2026-08 intensity pass. 20px/0.7s/power2.out read as a quiet drift; the hero
   is the first thing on the page and the motion was easy to miss entirely.
   56px so the travel is unmistakable, 0.55s because a faster arrival reads as
   more energetic than a long float, and back.out so each element passes its
   resting position and settles - on large type that lands as confidence rather
   than as a bounce. STAGGER stays at 0.15: there are five sequential steps here
   and widening it pushes the product rail past a second. */
const DURATION = 0.55
const STAGGER = 0.15
const SHIFT = 56
const EASE = 'back.out(1.6)'
// The two CTAs are the only multi-element step; 0.08 -> 0.11 so they read as two.
const CTA_STAGGER = 0.11

/* Globe plate. GLOBE_PEAK is the masked plate's outer opacity; the centre is
   multiplied down by the mask (see GLOBE_CENTRE_ALPHA), so effective centre
   opacity is GLOBE_PEAK * 0.20 ~= 0.13, i.e. below the 0.25 it
   ships at. Both numbers are contrast-bound, not taste - re-measure the hero
   copy if either moves. */
const GLOBE_SEL = '#page-home .hero-video'
const GLOBE_PEAK = 0.66
const GLOBE_TROUGH = 0.54
const GLOBE_REVEAL = 1.6
const GLOBE_BREATH = 6

/* The .word neutraliser. Injected once at module scope rather than from an
   effect so it is in the stylesheet before HtmlPage writes the hero into the
   DOM - from an effect it would land a frame late and the split words could
   paint at opacity 0 first. Scoped to the home hero so runHeroChars keeps
   working anywhere else it is used. */
const HERO_INTRO_CSS = `
#page-home section.hero-shell [data-hero-h1] .word {
  opacity: 1 !important;
  transform: none !important;
  transition: none !important;
}
/* Radial mask on the globe plate: alpha ~0.3 through the middle, full at the
   edges. This is what makes a brighter globe safe - the copy sits in the middle,
   so the field under it gets DARKER than the unmasked 0.25 while the limb and
   the connection arcs come up. Centred at 42% vertically because the Earth's
   bright limb sits below the midline in this footage.
   Only applied once GSAP has taken the plate over (the .pb-globe-lit class), so
   with no JS the video keeps its original flat 0.25 and nothing regresses. */
#page-home .hero-video.pb-globe-lit {
  -webkit-mask-image: radial-gradient(ellipse 78% 62% at 50% 42%,
    rgba(0,0,0,0.20) 0%, rgba(0,0,0,0.28) 40%, rgba(0,0,0,0.72) 66%, #000 86%);
          mask-image: radial-gradient(ellipse 78% 62% at 50% 42%,
    rgba(0,0,0,0.20) 0%, rgba(0,0,0,0.28) 40%, rgba(0,0,0,0.72) 66%, #000 86%);
  will-change: opacity, transform;
}
`
if (typeof document !== 'undefined' && !document.getElementById('pb-hero-intro-css')) {
  const el = document.createElement('style')
  el.id = 'pb-hero-intro-css'
  el.textContent = HERO_INTRO_CSS
  document.head.appendChild(el)
}

function findHero() {
  // #page-home is the home route's <main>. Scoping to it keeps the selectors
  // (.t-eyebrow, .btn, .t-lead) from matching the same class names on any
  // other route that happens to be mounted alongside it.
  const hero = document.querySelector('#page-home section.hero-shell')
  if (!hero || !hero.querySelector('[data-hero-h1]')) return null
  return hero
}

export default function HeroIntro() {
  // Survives the StrictMode double-invoke in dev and any remount, so the
  // timeline is built exactly once per page load.
  const played = useRef(false)

  useGSAP(() => {
    if (played.current) return
    // Only the home route has this hero. On a deep link straight to a subpage
    // there is nothing to animate and the observer below just times out.
    if (window.location.pathname !== '/') return

    let observer = null
    let timeoutId = 0

    const start = (hero) => {
      if (played.current) return
      played.current = true

      const groups = STEPS
        .map(s => ({ ...s, els: s.all ? Array.from(hero.querySelectorAll(s.sel)) : [hero.querySelector(s.sel)].filter(Boolean) }))
        .filter(g => g.els.length)
      if (!groups.length) return

      const all = groups.flatMap(g => g.els)

      // matchMedia gives the reduced-motion branch its own context, so the
      // tweens are reverted correctly if the user flips the OS setting.
      const mm = gsap.matchMedia()

      mm.add('(prefers-reduced-motion: reduce)', () => {
        // Motion is the thing being objected to, not the content. Show
        // everything immediately rather than animating a shorter distance.
        gsap.set(all, { opacity: 1, y: 0, clearProps: 'all' })
        // The globe plate is display:none under `reduce` (StyleOverrides), so it
        // is deliberately left alone here - no class, no tween, nothing to undo.
      })

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        // Lock the start state in the same frame the markup lands (see the
        // MutationObserver note above) so nothing flashes in at full opacity.
        gsap.set(all, { opacity: 0, y: SHIFT, willChange: 'transform, opacity' })

        const tl = gsap.timeline({
          defaults: { duration: DURATION, ease: EASE },
          // willChange on ~7 nodes for the whole scroll session is worse than
          // useless once the tween is done; hand the layers back.
          onComplete: () => gsap.set(all, { clearProps: 'willChange' }),
        })

        groups.forEach((g, i) => {
          tl.to(
            g.els,
            {
              opacity: 1,
              y: 0,
              // The CTAs are the only multi-element step; give them a visible
              // offset from each other so they read as two arrivals, not one.
              stagger: g.key === 'ctas' ? CTA_STAGGER : 0,
            },
            i === 0 ? 0 : `<${STAGGER}`,   // start STAGGER after the previous step began
          )
        })

        /* Globe: revealed alongside the copy, then breathing very slowly. Kept
           OUT of the copy timeline - it is atmosphere on a different clock
           (1.6s in, then a 12s round trip), and folding it in would drag the
           timeline's onComplete out to the end of an infinite tween. */
        const globe = document.querySelector(GLOBE_SEL)
        let globeIn = null
        let globeBreath = null
        if (globe) {
          globe.classList.add('pb-globe-lit')
          globeIn = gsap.fromTo(globe,
            { opacity: 0, scale: 1.1 },
            {
              opacity: GLOBE_PEAK, scale: 1, duration: GLOBE_REVEAL, ease: EASE,
              onComplete() {
                globeBreath = gsap.to(globe, {
                  opacity: GLOBE_TROUGH,
                  duration: GLOBE_BREATH,
                  ease: 'sine.inOut',
                  yoyo: true,
                  repeat: -1,
                })
              },
            },
          )
        }

        /* ── Follow the node in and out of the document ──────────────────────
           Leaving home does not hide #page-home, it DETACHES it (PB_parkPage
           in Pentabay.html), and this component never unmounts - so without
           this, the reveal and the infinite breathing tween keep ticking for
           the rest of the session, rewriting inline opacity every frame on a
           <video> that is not in the document and holding its composited
           layer open. That leaked as a stale hero plate painted over the next
           route until a reload forced a full repaint.

           Paired with the park/mount events rather than a pathname check:
           parking is keyed off the element, and the same node comes back on
           the way home, tween progress intact. */
        const syncGlobe = () => {
          if (!globe) return
          const live = document.contains(globe)
          ;[globeIn, globeBreath].forEach(t => {
            if (!t) return
            if (live) t.resume()
            else t.pause()
          })
        }
        window.addEventListener('pb-page-parked', syncGlobe)
        window.addEventListener('pb-pages-mounted', syncGlobe)

        return () => {
          window.removeEventListener('pb-page-parked', syncGlobe)
          window.removeEventListener('pb-pages-mounted', syncGlobe)
          tl.kill()
          if (globeIn) globeIn.kill()
          if (globeBreath) globeBreath.kill()
          if (globe) {
            globe.classList.remove('pb-globe-lit')
            gsap.set(globe, { clearProps: 'opacity,transform,willChange' })
          }
        }
      })
    }

    const immediate = findHero()
    if (immediate) {
      start(immediate)
      return
    }

    observer = new MutationObserver(() => {
      const hero = findHero()
      if (!hero) return
      observer.disconnect()
      observer = null
      start(hero)
    })
    observer.observe(document.body, { childList: true, subtree: true })

    // Nothing showed up - stop watching the whole document rather than leaving
    // a subtree observer running for the life of the session.
    timeoutId = window.setTimeout(() => {
      if (observer) { observer.disconnect(); observer = null }
    }, 10000)

    return () => {
      if (observer) observer.disconnect()
      window.clearTimeout(timeoutId)
    }
  }, { dependencies: [] })   // [] => builds on mount only, never on re-render

  return null
}
